import { h, setChildren } from "../core/dom.js";
import { backLink } from "../components/common.js";
import { navigate } from "../core/router.js";
import { profile, loadLibrary, getBodyMap } from "../store.js";
import { api } from "../api.js";
import {
  strengthLevel, liftFor, standardsTable, STRENGTH_LEVELS, STANDARDS_SOURCES,
} from "../lib/standards.js";

// Strength Standards reference — explains how the app turns your lifts into a training level
// (Untrained→Elite) and shows the documented bodyweight-ratio bands per main lift + common variant,
// linked to their catalog entries. Personalised: if your bodyweight + records are known, your own
// current ratio and band are shown next to each lift. Reached from the Dashboard PR board.

const COLS = ["Beginner", "Intermediate", "Advanced", "Elite"]; // table columns (Untrained = below Beginner)

export default function Standards() {
  const root = h("div", { class: "screen" });

  // Sex picks the threshold set (women scaled per lift — see fScale in standards.js). Default from the profile / body-map
  // choice; the user can flip it to compare.
  let sex = getBodyMap();

  let libIndex = null;        // lowercase catalog name -> id, for deep links
  const myLifts = {};         // lift key -> { ratio, exercise_name } (best per lift from records)

  // Best-effort enrichment — the static table renders immediately regardless.
  loadLibrary()
    .then((lib) => { libIndex = new Map((lib || []).map((e) => [(e.exercise_name || "").toLowerCase(), e.id])); render(); })
    .catch(() => {});

  const bw = profile()?.bodyweight_kg;
  if (bw && bw > 0) {
    api.records(50)
      .then((recs) => {
        for (const r of recs || []) {
          const lift = liftFor(r.exercise_name);
          if (!lift || !r.estimated_1rm_kg) continue;
          const ratio = r.estimated_1rm_kg / bw;
          if (!myLifts[lift.key] || ratio > myLifts[lift.key].ratio) myLifts[lift.key] = { ratio, exercise_name: r.exercise_name };
        }
        render();
      })
      .catch(() => {});
  }

  render();
  return root;

  function sexToggle() {
    const btn = (val, label) =>
      h("button", {
        style: {
          padding: "6px 14px", borderRadius: "999px", border: "1px solid var(--border)", cursor: "pointer",
          background: sex === val ? "var(--accent)" : "var(--surface-2)",
          color: sex === val ? "var(--on-accent)" : "var(--text-2)", fontWeight: 600, fontSize: ".85rem",
        },
        onClick: () => { sex = val; render(); },
      }, label);
    return h("div", { class: "hstack", style: { gap: "8px", alignItems: "center" } },
      h("span", { class: "dim", style: { fontSize: ".82rem" } }, "Standards for:"),
      btn("male", "Men"), btn("female", "Women"));
  }

  function legend() {
    return h("div", { class: "card" },
      ...STRENGTH_LEVELS.map((lv) =>
        h("div", { class: "row", style: { padding: "7px 0", gap: "10px", alignItems: "flex-start", borderTop: lv.name === "Untrained" ? "none" : "1px solid var(--border)" } },
          h("span", { style: { flex: "0 0 auto", marginTop: "3px", width: "10px", height: "10px", borderRadius: "50%", background: lv.color } }),
          h("div", { style: { minWidth: 0 } },
            h("span", { style: { fontWeight: 700, color: lv.color } }, lv.name),
            h("div", { class: "dim", style: { fontSize: ".82rem", lineHeight: "1.4", marginTop: "1px" } }, lv.blurb)))));
  }

  // Which band index (1..4 → Beginner..Elite) the user currently sits in for a lift, else Untrained/null.
  function myBandIndex(rec) {
    if (!rec) return null;
    const lv = strengthLevel(rec.exercise_name, rec.ratio, sex);
    if (!lv) return null;
    return { idx: STRENGTH_LEVELS.findIndex((x) => x.name === lv.level), level: lv }; // idx 0 (Untrained) … 4 (Elite)
  }

  function liftTable(rows) {
    const th = (txt, extra = {}) => h("th", { style: { padding: "8px 6px", textAlign: "right", fontSize: ".74rem", fontWeight: 700, ...extra } }, txt);
    const head = h("tr", null,
      h("th", { style: { padding: "8px 6px", textAlign: "left", fontSize: ".74rem", color: "var(--text-2)" } }, "Lift"),
      ...COLS.map((c, i) => th(c, { color: STRENGTH_LEVELS[i + 1].color })));

    const body = rows.map((row) => {
      const id = libIndex && libIndex.get((row.catalog || "").toLowerCase());
      const mine = myBandIndex(myLifts[row.key]);
      const nameCell = h("td", { style: { padding: "9px 6px", textAlign: "left", verticalAlign: "top" } },
        id
          ? h("a", { style: { fontWeight: 700, color: "var(--accent)", cursor: "pointer" }, onClick: () => navigate(`/exercise/${id}`) }, row.name)
          : h("span", { style: { fontWeight: 700 } }, row.name),
        row.note ? h("div", { class: "dim", style: { fontSize: ".72rem", marginTop: "1px" } }, row.note) : null,
        mine
          ? h("div", { style: { fontSize: ".76rem", marginTop: "2px", color: mine.level.color, fontWeight: 600 } },
              `you: ${myLifts[row.key].ratio.toFixed(2)}× · ${mine.level.level}`)
          : null);
      const cells = row.t.map((v, i) => {
        const here = mine && mine.idx === i + 1; // the user's current band column
        return h("td", {
          style: {
            padding: "9px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums",
            background: here ? "var(--surface-2)" : "transparent", borderRadius: here ? "6px" : "0",
            fontWeight: here ? 800 : 500, color: here ? STRENGTH_LEVELS[i + 1].color : "var(--text)",
          },
        }, `${v.toFixed(2)}×`);
      });
      return h("tr", { style: { borderTop: "1px solid var(--border)" } }, nameCell, ...cells);
    });

    return h("div", { style: { overflowX: "auto" } },
      h("table", { style: { width: "100%", borderCollapse: "collapse", fontSize: ".88rem", minWidth: "360px" } },
        h("thead", null, head), h("tbody", null, ...body)));
  }

  function howCard() {
    return h("div", { class: "card" },
      h("ol", { style: { margin: 0, paddingLeft: "20px", fontSize: ".86rem", lineHeight: "1.5" } },
        h("li", null, "We estimate your 1-rep max for a lift from your best logged set (Epley formula)."),
        h("li", null, "We divide that by your bodyweight to get a strength-to-bodyweight ratio."),
        h("li", null, "That ratio is matched to the bands below for that lift — below Beginner is Untrained."),
        h("li", null, "Your ", h("b", null, "overall level"), " (shown on the dashboard and Coach) is the average band across the lifts you train.")),
      h("p", { class: "dim", style: { fontSize: ".8rem", lineHeight: "1.45", margin: "10px 0 0" } },
        "Women's thresholds are scaled per lift, since the sex gap differs by movement — pressing to about 58–62% of men's numbers, rows to 65%, squat and deadlift to 75% (see Sources). Numbers are multiples of bodyweight (×BW)."));
  }

  // Directly answers "do age, height & bodyweight matter?" — personalised by age when known.
  function ageBodyCard() {
    const age = profile()?.age;
    const ageLine = age == null
      ? "These are general adult standards; strength peaks in the 20s–30s and gradually declines after ~40–50."
      : age >= 50
        ? `You're ${age}. Strength gradually declines after ~40–50, and these are general adult standards — so read a given band as more impressive than the number alone. (Your Coach factors your age into its guidance.)`
        : age >= 40
          ? `You're ${age} — close to the broad peak-strength window (20s–40s). Strength declines slowly after this, but the standards stay age-neutral.`
          : `You're ${age} — within the typical peak-strength window (20s–30s).`;
    const row = (label, text) =>
      h("div", { style: { padding: "8px 0", borderTop: "1px solid var(--border)" } },
        h("span", { style: { fontWeight: 700, fontSize: ".85rem" } }, label),
        h("div", { class: "dim", style: { fontSize: ".82rem", lineHeight: "1.45", marginTop: "1px" } }, text));
    return h("div", { class: "card" },
      h("div", { style: { fontWeight: 700, fontSize: ".9rem", marginBottom: "2px" } }, "Do age, height & bodyweight matter?"),
      h("div", { style: { paddingTop: "2px" } },
        row("Bodyweight — yes, it's built in", "Every band is a multiple of your bodyweight. Heavier lifters naturally show lower ratios (strength rises more slowly than bodyweight), so treat the bands as a rough cross-bodyweight guide."),
        row("Age — matters, but we don't bend the numbers", ageLine),
        row("Height & limb length — a caveat, not an adjustment", "Long arms make the bench harder and the deadlift easier (short limbs the reverse). That's an individual quirk no standard adjusts for.")));
  }

  function sourcesCard() {
    return h("div", { class: "card" },
      ...STANDARDS_SOURCES.map((c, i) =>
        h("div", { style: { padding: "9px 0", borderTop: i === 0 ? "none" : "1px solid var(--border)" } },
          h("div", { style: { fontWeight: 700, fontSize: ".85rem" } }, c.source),
          c.finding ? h("div", { class: "dim", style: { fontSize: ".82rem", lineHeight: "1.45", marginTop: "2px" } }, c.finding) : null,
          c.url ? h("a", { href: c.url, target: "_blank", rel: "noopener noreferrer", style: { fontSize: ".8rem", color: "var(--accent)", display: "inline-block", marginTop: "4px" } }, "View source ↗") : null)));
  }

  function render() {
    const hasBw = !!(profile()?.bodyweight_kg > 0);
    const rows = standardsTable(sex);
    const baseRows = rows.filter((r) => !r.variant);
    const variantRows = rows.filter((r) => r.variant);

    setChildren(root,
      backLink("Progress", "/history"),
      h("h1", { style: { margin: "4px 0" } }, "Strength standards"),
      h("p", { class: "dim", style: { marginTop: 0, fontSize: ".85rem", lineHeight: "1.5" } },
        "How your training level is worked out, and the documented bodyweight-ratio bands behind it."),

      h("div", { class: "section-title" }, "How your level is worked out"),
      howCard(),

      !hasBw
        ? h("div", { class: "card card-tap", style: { marginTop: "10px" }, onClick: () => navigate("/weight") },
            h("div", { style: { fontWeight: 700 } }, "Add your bodyweight to see your own level"),
            h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } }, "Levels are relative to bodyweight. Log it on the Weight screen → tap to open."))
        : null,

      h("div", { class: "section-title", style: { marginTop: "14px" } }, "The bands"),
      legend(),

      h("div", { class: "section-title", style: { marginTop: "14px" } }, "Bodyweight-ratio standards"),
      sexToggle(),
      h("div", { class: "card", style: { marginTop: "8px" } }, liftTable(baseRows)),

      h("div", { class: "section-title", style: { marginTop: "14px" } }, "Common variants"),
      h("p", { class: "dim", style: { marginTop: 0, fontSize: ".82rem", lineHeight: "1.5" } },
        "Variants are scored on their own standard (derived from the main lift via a typical strength ratio), so they're judged fairly rather than against the barbell numbers. Dumbbell figures are the combined weight of both dumbbells — the total you log."),
      h("div", { class: "card" }, liftTable(variantRows)),

      h("div", { class: "section-title", style: { marginTop: "14px" } }, "Age, height & bodyweight"),
      ageBodyCard(),

      h("div", { class: "section-title", style: { marginTop: "14px" } }, "Sources"),
      sourcesCard(),

      h("p", { class: "dim", style: { fontSize: ".74rem", lineHeight: "1.45", margin: "12px 4px 0" } },
        "These are population averages and vary with bodyweight, age, limb lengths and training history — a guide for context, not a clinical or medical threshold. Variant ratios are typical averages and individuals differ."));
  }
}
